<script setup>
import {useAuthStore} from '~/store/auth'
import {useCourseApi} from '~/composables/api/useCourseApi'
import {Form, useForm} from 'vee-validate'
import CheckField from '~/components/forms/subelements/CheckField.vue'
import Button from '~/components/common/Button.vue'
import InputField from '~/components/forms/subelements/InputField.vue'
import {cloneDeep} from 'lodash'
import {omit} from 'lodash/object'
import {objectHasChanges} from '~/utils/objectUtils'
import * as yup from 'yup'

const requestDataSchema = yup.object({
  firstname: yup.string().required().label('Nombre'),
  lastname: yup.string().required().label('Apellidos'),
  email: yup.string().email().required().label('Email'),
  phone: yup.string().optional().label('Teléfono'),
  terms: yup
    .boolean()
    .required()
    .isTrue('Debes aceptar la política de privavidad')
    .label('Términos y Condiciones'),
})

const bus = useEventBus('dialogs')
const emit = defineEmits(['close'])
const authStore = useAuthStore()
const sending = ref(false)
const loaded = ref(true)
const showDialog = ref(false)
const notification = useNotification()
const currentProduct = ref(null)
const currentUser = computed(() => authStore.user)
const courseApi = useCourseApi()
const modalTitle = computed(() => {
  return currentProduct.value?.title || 'Solicitar curso'
})

const emptyRequestData = {
  referer: '',
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  terms: false
}

const originalValue = ref({
  firstname: '',
  lastname: '',
  email: '',
  phone: ''
})
const currentData = ref({
  firstname: '',
  lastname: '',
  email: '',
  phone: ''
})

const {validate} = useForm({
  validationSchema: requestDataSchema,
})

const hasChanges = computed(() => {
  return objectHasChanges(currentData.value, originalValue.value)
})

async function open(product = null) {
  if (!product) return

  currentProduct.value = cloneDeep(product)
  initFormData()
  showDialog.value = true
}

function close() {
  showDialog.value = false
}

function initFormData() {
  const data = cloneDeep(emptyRequestData)
  data.referer = window.location.href
  data.firstname = currentUser.value?.firstname || ''
  data.lastname = currentUser.value?.lastname || ''
  data.email = currentUser.value?.email || ''
  data.phone = currentUser.value?.phone || ''

  originalValue.value = cloneDeep(data)
  currentData.value = cloneDeep(data)
}

function hideModal() {
  showDialog.value = false
}

async function onSubmit() {
  try {
    if (sending.value) return

    sending.value = true

    const formData = omit(currentData.value, ['terms'])
    formData.subject = `${currentProduct.value.title} - Solicitud de información`
    formData.message = `${formData.firstname} ${formData.lastname} está interesado en el curso ${currentProduct.value.title}.`

    await courseApi.requestInformation(formData)
    initFormData()

    notification.showSuccess(`La solicitud se ha enviado correctamente. Te responderemos en breve.`)

    hideModal()
  } catch (error) {
    notification.showError(error)
  } finally {
    sending.value = false
  }
}


function busListener(event, payload = null) {
  const dialogEvent = 'request-information'
  if (event === `open-${dialogEvent}`) {
    bus.emit('close-others', dialogEvent)

    open(payload)
  }

  if (event === 'close-others') {
    if (dialogEvent !== payload) {
      close()
    }
  }
}

onMounted(async () => {
  initFormData()
  loaded.value = true
})

onBeforeMount(() => {
  bus.on(busListener)
})

</script>

<template>
  <Dialog
    @update:visible="close()"
    :close-on-escape="false"
    :header="modalTitle"
    modal
    :visible.sync="showDialog"
    class="w-full max-w-lg"
    :draggable="false"
  >
    <Form
      v-if="loaded && currentData"
      id="requestDataForm"
      ref="requestDataFormRef"
      class="grid grid-cols-12 lg:px-8 gap-4 py-4 !px-0"
      :validation-schema="requestDataSchema"
      @submit="onSubmit"
    >
      <InputField
        :disabled="authStore.isAuth"
        class="col-span-12"
        label="Nombre"
        inputId="firstname"
        name="firstname"
        v-model="currentData.firstname"
      />

      <InputField
        :disabled="authStore.isAuth"
        class="col-span-12"
        label="Apellidos"
        inputId="lastname"
        name="lastname"
        v-model="currentData.lastname"
      />

      <InputField
        :disabled="authStore.isAuth"
        class="col-span-12"
        type="email"
        label="Email"
        inputId="email"
        name="email"
        v-model="currentData.email"
      />

      <InputField
        :disabled="authStore.isAuth && !!currentUser.phone"
        class="col-span-12"
        type="tel"
        label="Teléfono"
        inputId="phone"
        name="phone"
        v-model="currentData.phone"
      />

      <CheckField
        inputId="terms"
        name="terms"
        v-model="currentData.terms"
        class="col-span-12 mb-3 text-[0.9rem] lg:text-md"
      >
        He leído y acepto la
        <NuxtLink class="underline" href="/politica-de-privacidad" target="_blank">
          Política de Privacidad
        </NuxtLink>
      </CheckField>

      <div class="col-span-12 text-center mb-5 flex justify-end">
        <Button
          class="w-full"
          :disabled="!hasChanges || sending"
          :loading="sending"
          label="Solicitar Curso"
          type="submit"
        />
      </div>
    </Form>
  </Dialog>
</template>
